import { Component, inject, Input, OnDestroy, OnInit, signal } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { MarketService } from "../../../../../../../shared/src/lib/services/market.service";
import { Router } from "@angular/router";
import { LiveUpdateService } from "../../../../../../../shared/src/lib/services/live-update.service";
import { MatBottomSheetModule } from "@angular/material/bottom-sheet";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatButtonModule } from "@angular/material/button";
import { OverlayComponent } from "../../../../components/overlay/overlay.component";
import { getStatus } from "../../../../../../../shared/src/lib/utils/market";
import { CoreService } from "../../../../../../../shared/src/lib/services/core.service";
import { BetPlacementService } from "../../../../../../../shared/src/lib/services/bet-placement.service";
import { TranslateModule } from "@ngx-translate/core";
import { MarketRunnersComponent } from "./market-runners/market-runners.component";
import { MatChipsModule } from "@angular/material/chips";
import { LoaderService } from "../../../../../../../shared/src/lib/services/loader.service";
import { SnackbarService } from "../../../../../../../shared/src/lib/services/snackbar.service";
import { MarketOpsService } from "./market-ops.service";
import { layLessSports } from "../../../../../assets/constants/market";
import { NavService } from "../../../../../../../shared/src/lib/services/nav.service";
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from "@angular/material/expansion";
import { AsianHandicapPnlComponent } from "./asian-handicap-pnl/asian-handicap-pnl.component";
import { DecimalPipe, NgClass } from "@angular/common";
import { MyMarketsBfHeaderComponent } from "../../my-markets/my-markets-bf-header/my-markets-bf-header.component";
import { MyMarketsService } from "../../my-markets/my-markets.service";
import { CashOutOfferComponent } from "./cash-out-offer/cash-out-offer.component";
import { MarketCountdownComponent } from "../../components/market-countdown/market-countdown.component";
import { ExRateService } from "../../../../../../../shared/src/lib/services/ex-rate.service";
import { BlockOverlayComponent } from "../../../../components/block-overlay/block-overlay.component";
import { WhiteLabelConfigService } from "../../../../../../../shared/src/lib/services/white-label-config.service";
import { FeatureFlagService } from "../../../../../../../shared/src/lib/services/feature-flag.service";
import { takeUntil } from "rxjs/operators";
import { FavoritesService } from "../../../../../../../shared/src/lib/services/favorites.service";

const favoriteType = "MARKET";

@Component({
  selector: "app-market",
  imports: [
    MatIconModule,
    MatTooltipModule,
    MatBottomSheetModule,
    MarketRunnersComponent,
    MatButtonModule,
    OverlayComponent,
    TranslateModule,
    MatChipsModule,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    AsianHandicapPnlComponent,
    DecimalPipe,
    MyMarketsBfHeaderComponent,
    CashOutOfferComponent,
    CashOutOfferComponent,
    MarketCountdownComponent,
    BlockOverlayComponent,
    NgClass,
  ],
  templateUrl: "./market.component.html",
  styleUrl: "./market.component.scss",
})
export class MarketComponent implements OnInit, OnDestroy {
  @Input() data: any = null;
  @Input() headerType: "my-markets" | "normal" = "normal";
  loader = inject(LoaderService);
  marketService = inject(MarketService);
  liveUpdateService = inject(LiveUpdateService);
  coreService = inject(CoreService);
  betPlacementService = inject(BetPlacementService);
  placementService = inject(BetPlacementService);
  snackbar = inject(SnackbarService);
  marketOps = inject(MarketOpsService);
  wlConfigService = inject(WhiteLabelConfigService);
  router = inject(Router);
  exRateService = inject(ExRateService);
  readonly destroy$ = new Subject<void>();
  marketUpdate = signal(null);
  marketStatus = signal("");
  isInPlay = signal<boolean>(false);
  totalMatched = signal<number>(0);
  runnerUpdates = new BehaviorSubject<any>([]);
  marketId: any;
  exRate: any = 1;
  $loading!: Observable<boolean>;
  navService = inject(NavService);
  myMarketsService = inject(MyMarketsService);
  featureFlagService = inject(FeatureFlagService);
  showCashOut = true;
  allowPlacement = true;
  timeDiffCheckInterval: any = null;
  favoritesService = inject(FavoritesService);

  constructor() {}

  get isLine() {
    return this.data?.description?.bettingType === "LINE";
  }

  get hasCashOut() {
    return !layLessSports.includes(this.data?.eventType?.id);
  }

  get showStartTime() {
    return layLessSports.includes(this.data?.eventType?.id);
  }

  get isAsianHandicap() {
    return this.data?.description?.bettingType?.includes("ASIAN_HANDICAP");
  }

  get hasMoreThan3Runners() {
    return this.data?.runners?.length > 3;
  }

  get isDoubleChance() {
    return this.data?.description?.marketType === "DOUBLE_CHANCE";
  }

  get exRateValue() {
    const exchangeRate = this.exRateService.exRate$.getValue();
    return exchangeRate?.RATE ?? 1;
  }

  get isFavorite() {
    return this.favoritesService.isFavorite(this.data?.marketId, favoriteType);
  }

  ngOnInit() {
    if (this.data) {
      this.getMarketUpdates(this.data?.marketId);
      this.totalMatched.set(this.data?.totalMatched);
      this.handleMarketUpdate(this.data?.mc);
      if (this.hasMoreThan3Runners) this.handleCashOutFlag();
      this.checkPreMatchPlacement();
    }
  }

  checkPreMatchPlacement() {
    const openDate = this.data?.event?.openDate;
    const placementPermitted = this.wlConfigService.showFeature("preMatchPlacement");
    this.allowPlacement = !placementPermitted && openDate ? this.wlConfigService.isWithin45Minutes(openDate) : true;
    if (!placementPermitted && openDate) {
      this.timeDiffCheckInterval = setInterval(() => {
        // console.log(this.marketOps.isWithin45Minutes(this.data?.event?.openDate));
        this.allowPlacement = this.wlConfigService.isWithin45Minutes(openDate);
      }, 5000);
    }
  }

  getMarketUpdates(marketId: string) {
    this.liveUpdateService
      .getData("market", { field: "id", value: marketId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (data) this.handleMarketUpdate(data);
        },
      });
  }

  handleCashOutFlag() {
    this.featureFlagService
      .showFeature("cashOutForMarketsOver3Runners", true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.showCashOut = value;
      });
  }

  handleMarketUpdate(update: any) {
    const status = update?.st !== undefined ? getStatus(update?.st) : "";
    if (status === "Closed") {
      this.myMarketsService.removeOpenBetsMarket(update?.id);
      this.marketOps.removeMarket(update?.id, "marketId");
    }
    this.marketUpdate.set(update);
    this.marketStatus.set(status);
    if (update?.ipl) this.isInPlay.set(update?.ipl);
    if (update?.tv) this.totalMatched.set(update?.tv);
    if (update?.r && update?.r?.length > 0) {
      this.runnerUpdates.next(update?.r);
    }
  }

  handleFavorites(e: Event) {
    e.stopPropagation();
    e.preventDefault();
    const favoriteItem = {
      id: this.data?.marketId,
      type: favoriteType,
      name: this.data?.marketName,
      marketType: this.data?.description?.marketType,
      parents: [this.data?.event],
    };
    if (this.isFavorite) {
      this.favoritesService.remove(favoriteItem, favoriteItem?.type);
    } else {
      this.favoritesService.add(favoriteItem, favoriteItem?.type);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.timeDiffCheckInterval) clearInterval(this.timeDiffCheckInterval);
  }
}
